import { Loader2, Power, PowerOff, Crown, ShieldCheck, User, BadgeCheck } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  Button,
} from '@ncoder/ui';
import { StatusBadge } from '@/components/atoms/org-admin/StatusBadge';
import { MemberAvatar } from '@/components/atoms/org-admin/MemberAvatar';
import type { ManagementLevel, OrgRole } from '@/services/org-admin';

const LEVEL_ICON: Record<ManagementLevel, typeof Crown> = {
  owner: Crown,
  admin: ShieldCheck,
  member: User,
};

const PILL_TRIGGER_CLASSNAME =
  'h-7 w-auto min-w-0 gap-1.5 rounded-full border-none bg-muted px-2.5 py-0 text-xs font-medium shadow-none hover:bg-muted/70 focus:ring-1 focus:ring-ring focus:ring-offset-0';

// Anchor below the trigger (Radix's default "item-aligned" mode opens in an
// unpredictable direction/position) and scroll instead of overflowing once
// the list is taller than the viewport allows.
const SELECT_CONTENT_PROPS = {
  position: 'popper' as const,
  sideOffset: 4,
  className: 'max-h-60 w-[--radix-select-trigger-width] overflow-y-auto',
};

export interface MemberRowProps {
  memberId: string;
  userId: string;
  email: string;
  // Derived from the assigned role; not independently editable here.
  managementLevel: ManagementLevel | null;
  status: string;
  roleId: string | null;
  roleName: string | null;
  isSelf: boolean;
  roles: OrgRole[];
  isAssigningRole: boolean;
  isTogglingActive: boolean;
  onAssignRole: (roleId: string) => void;
  onToggleActive: () => void;
}

export function MemberRow({
  userId,
  email,
  managementLevel,
  status,
  roleId,
  roleName,
  isSelf,
  roles,
  isAssigningRole,
  isTogglingActive,
  onAssignRole,
  onToggleActive,
}: MemberRowProps) {
  const isActive = status.toLowerCase() === 'active';
  const LevelIcon = (managementLevel && LEVEL_ICON[managementLevel]) ?? User;

  return (
    <div className="flex items-center justify-between gap-4 border-b border-border/60 px-4 py-3 last:border-0">
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <MemberAvatar email={email} userId={userId} />
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="truncate text-sm font-medium">{email}</span>
            {isSelf && (
              <span className="shrink-0 text-xs text-muted-foreground">(you)</span>
            )}
          </div>
          <div className="mt-1 flex items-center gap-1.5">
            <StatusBadge status={status} />
            <span className="flex items-center gap-1 text-xs capitalize text-muted-foreground">
              <LevelIcon className="h-3 w-3" />
              {managementLevel ?? 'No role'}
            </span>
          </div>
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-2">
        <Select
          value={roleId ?? ''}
          onValueChange={(value) => onAssignRole(value)}
          disabled={isAssigningRole}
        >
          <SelectTrigger className={PILL_TRIGGER_CLASSNAME}>
            {isAssigningRole ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <span className="flex items-center gap-1.5">
                <BadgeCheck className="h-3.5 w-3.5" />
                {roleName ?? 'Select role'}
              </span>
            )}
          </SelectTrigger>
          <SelectContent {...SELECT_CONTENT_PROPS}>
            {roles.map((role) => (
              <SelectItem key={role.id} value={role.id}>
                {role.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Button
          variant="ghost"
          size="icon"
          onClick={onToggleActive}
          disabled={isSelf || isTogglingActive}
          title={isSelf ? 'You cannot deactivate yourself' : isActive ? 'Deactivate member' : 'Reactivate member'}
          className={isActive ? 'text-muted-foreground hover:text-destructive' : 'text-muted-foreground hover:text-success'}
        >
          {isTogglingActive ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : isActive ? (
            <PowerOff className="h-4 w-4" />
          ) : (
            <Power className="h-4 w-4" />
          )}
        </Button>
      </div>
    </div>
  );
}
